import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { LookScene } from "@/data/lookScenes";
import { ArticlePicksBlock } from "@/components/journal/ArticlePicksBlock";
import { rememberEditorialSource } from '@/lib/editorialAttribution';

/**
 * Hotspot markers are real anchors (react-router <Link> renders an <a href>) so
 * they can be opened in a new tab, are announced as links, and pass link equity
 * to the categories they point at.
 */
const Hotspot = ({
  x,
  y,
  label,
  category,
  productHandle,
  productAvailable,
}: {
  x: number;
  y: number;
  label: string;
  category: string;
  productHandle?: string;
  productAvailable: boolean;
}) => (
  <div
    className="absolute group"
    style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
  >
    <Link
      to={productHandle && productAvailable ? `/product/${productHandle}` : category}
      aria-label={label}
      className="relative flex h-11 w-11 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {/* Soft pulsing ring — sized to the visible dot, not the 44px hit area */}
      <span className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/60 animate-ping [animation-duration:2.4s] md:h-8 md:w-8" />
      <span className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background/70 backdrop-blur-[2px] border border-primary/60 transition-transform duration-200 group-hover:scale-110 md:h-8 md:w-8" />
      <span className="relative h-1.5 w-1.5 rounded-full bg-primary" />
    </Link>

    {/* Decorative label chip — revealed on hover/focus of the marker */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-full bg-foreground px-3 py-1.5 text-[11px] font-semibold tracking-wide text-background shadow-md opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0"
    >
      {label}
    </span>
  </div>
);


export const GetTheLookScene = ({
  scene,
  priority = false,
  picks = [],
}: {
  scene: LookScene;
  priority?: boolean;
  /** Verified, in-stock product handles in hotspot order. */
  picks?: string[];
}) => (
  <figure className="relative" onClickCapture={(event) => {
    if (event.target instanceof Element && event.target.closest('a')) rememberEditorialSource('room', scene.slug);
  }}>
    <div className="relative overflow-hidden bg-muted aspect-[3/2] md:aspect-auto">
      <img
        src={scene.image}
        alt={scene.title}
        loading={priority ? "eager" : "lazy"}
        className="w-full h-full md:h-auto block object-cover"
      />
      {scene.hotspots.map((h) => (
        <Hotspot key={`${h.x}-${h.y}-${h.label}`} {...h} productAvailable={Boolean(h.productHandle && picks.includes(h.productHandle))} />
      ))}
    </div>
    <figcaption className="w-[89%] max-w-[1140px] mx-auto py-6 md:py-8 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
      <div>
        <h2 className="font-display text-xl md:text-2xl tracking-[0.02em] text-foreground">
          {scene.title}
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl">{scene.caption}</p>
      </div>
      <Link
        to={scene.article}
        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-foreground hover:text-primary transition-colors shrink-0 group"
      >
        Read the guide
        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
      </Link>
    </figcaption>
    {/* The rail mirrors the product-linked hotspots in exactly the same order. */}
    {picks.length > 0 && (
      <div className="w-[89%] max-w-[1140px] mx-auto -mt-8">
        <ArticlePicksBlock handles={picks} heading="Shop the look" />
      </div>
    )}
  </figure>
);
